import { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { useNavigate, useSearchParams } from "react-router-dom"
import { get, toUpper, toLower, isEmpty } from "lodash"
import classNames from "classnames"
import { DATE, PERIOD, FILTER_ON_GROUP, SEARCHABLE_MENU_ITEM, FILTER } from "helpers/shapes"
import { Paper, Box } from "@mui/material"
import { InfoOutlined } from "@mui/icons-material"
import OPTooltip from "components/material/tooltip"
import AssetsHeader from "components/assets-header"
import AssetAnalyticsTable from "modules/analyze-composite-asset-analytics-table"
import ModelSelector from "modules/model-selector"
import BaseSelector from "components/base-selector"
import SearchableMenu from "components/searchable-menu"
import AnalyzeCompositeExposure from "modules/analyze-composite-exposure"
import AnalyzeCompositePerformance from "modules/analyze-composite-performance"
import AnalyzeCompositeRisk from "modules/analyze-composite-risk"
import AnalyzeCompositeComposition from "modules/analyze-composite-composition"
import AnalyzeCompositeForecast from "modules/analyze-composite-forecast"
import saveRecentResources from "hocs/saveRecentResources"
import AnalyzeFactorsRisk from "modules/analyze-factors-risk"
import AnalyzeFactorsPerformance from "modules/analyze-factors-performance"
import FactorsSubscreenSelector from "./components/factors-subscreen-selector"
import AnalyzeCompositeFactorSensitivity from "modules/analyze-composite-factor-sensitivity"
import ShareMyView from "modules/share-my-view"
import withModelAccessControl from "hocs/with-model-access-control"
import useMountEffect from "hooks/mount-effect"
import { addBaseToHistory } from "helpers/base"
import "./style.scss"

const SCREENS = [{
  value: "assets",
  label: "Assets",
}, {
  value: "trends",
  label: "Trends",
}]

function AnalyzeCompositeSecurity({
  account,
  modelId,
  asset,
  assetId,
  assetType,
  assetName,
  assetTickerMic,
  internalId,
  categories,
  mustAggregateFactors,
  factorsClassification,
  factorsMetadata,
  aggregatedFactorsMetadata,
  aggregationFunction,
  displayAddView,
  selectedViews,
  selectedViewsFactors,
  classifications,
  base,
  baseStartDate,
  baseSignature,
  baseHistory,
  contentSetId,
  forecastId,
  latestDate,
  earliestDate,
  startDate,
  endDate,
  selectedDate,
  setDate,
  driftDates,
  period,
  setPeriod,
  periodAdjusted,
  selectedDateAdjusted,
  addNotification,
  openCompositeSecurity,
  filterOns,
  filtersVisible,
  toggleFilters,
  filter,
  segment,
  availableRollups,
  currentGrouping,
  setGrouping,
  expand,
  setExpand,
  normalization,
  disableNormalization,
  disableNormalizationTooltip,
  isNormalizationAdjusted,
  setNormalization,
  selectedScreen,
  selectScreen,
  selectedSubScreen,
  setModel,
  selectedModelName,
  isActive,
  benchmark,
  selectedBase,
  setSelectedBase,
  baseSearchValue,
  baseDropdownVisible,
  baseInputFocus,
  baseInputBlur,
  baseSearch,
  defaultSelectedBase,
  searchResults,
  viewType,
  showNotHeld,
  setShowNotHeld,
  displayShowNotHeld,
  isOpPro,
  resetDate,
  analyzeMenuItems,
  menuItems,
  subMenuItems,
  favoriteFactors,
  modelSupportsComposites,
  performanceStartDate,
  currency,
  unsupportedCurrencyWarning,
  selectedAnalyzeView,
  factorSensitivityType,
  isModelInQueryString,
  setHeader,
  assetInfoContent,
  shareMyViewProps,
  saveSelectedBase,
  updateFilters,
  addedFilters,
}) {
  // Used to keep track of the AnalyzeFactors submodule loaded state
  const [loaded, setLoaded] = useState(false)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  useEffect(() => {
    // redirect to a URL that uses internal ID
    if (asset && assetType === "etf" && assetId !== get(asset, "internalId")) {
      navigate(`/${account}/composite/etf/${internalId}?${searchParams.toString()}`)
    }
  }, [account, asset, assetId, assetType, internalId, navigate, searchParams])

  useEffect(() => {
    if (!isModelInQueryString) {
      setModel(modelId)
    }
  }, [isModelInQueryString, setModel, modelId])

  useEffect(() => {
    // update the model selector manually since components in the header don't automatically get updated/re-rendered when props change
    setHeader(AnalyzeCompositeSecurity.getHeaderConfig({
      asset, assetInfoContent, modelId, shareMyViewProps,
    }))
  }, [setHeader, asset, assetInfoContent, modelId, shareMyViewProps])

  // Validate the selected base and save it to the history
  // If selectedBase is empty but the query string base parameter is present, it means the getSelectedBase
  // couldn't find a matching base in the loaded data
  // [BUG] this is forcing a navigate on any re-render that we don't need most of the time. See [APP-1263]
  useMountEffect(() => {
    const querystringBase = searchParams.get("base")
    const baseFound = !isEmpty(selectedBase) || isEmpty(querystringBase)
    let baseToSave = selectedBase
    let historyToSave = baseHistory

    // If the base specified in the query string is found in the loaded data, we want to add it to the history
    if (baseFound) {
      historyToSave = addBaseToHistory(baseHistory, baseToSave)
    } else {
      baseToSave = null
    }

    saveSelectedBase(selectedBase, historyToSave)

    if (!baseToSave && !isEmpty(querystringBase)) {
      addNotification("warning", "Benchmark not found", "The selected benchmark could not be found so it was ignored.")
    }
  })

  const handleSelectAnalyzeView = (view) => {
    const { type, path } = view
    let screen = "trends/"
    if (path) {
      screen += path
    } else {
      if (type) {
        screen += `${type}/`
      }
      screen += view.id
    }

    selectScreen(screen, selectedSubScreen)
  }

  const handleSelectScreen = item => {
    const { id } = item
    selectScreen(id, selectedSubScreen)
  }

  const customSelectScreen = <Paper square className="screen-menu-container">
    <Box className="screen-menu-container">
      <SearchableMenu items={menuItems} current={selectedScreen} onSelect={handleSelectScreen} hideSearchBox className="analyze-composite-select-screen" />
    </Box>
  </Paper>

  const positionSetId = assetTickerMic ? `${assetTickerMic.ticker}.${assetTickerMic.mic}` : assetId
  const analyzeViewSelectList = <SearchableMenu items={analyzeMenuItems} current={selectedAnalyzeView} onSelect={handleSelectAnalyzeView} className="analyze-view-select-list" />
  const upperAssetType = assetType === "basket" ? "SWAP" : toUpper(assetType)

  const commonTrendsAttributes = {
    ...{
      analyzeViewSelectList,
      account,
      modelId,
      positionSetId,
      positionSetType: assetType,
      positionSetName: assetName,
      isActive,
      base,
      baseStartDate,
      baseSignature,
      categories,
      classifications,
      segment,
      currentGrouping,
      setGrouping,
      expand,
      startDate,
      endDate,
      selectedDate,
      earliestDate,
      latestDate,
      driftDates,
      setDate,
      filtersVisible,
      viewType,
      showNotHeld,
      currency,
    },
  }

  const commonFactorReviewAttributes = {
    account,
    modelId,
    isActive,
    base,
    baseSignature,
    segment,
    selectedDate,
    startDate: performanceStartDate || startDate,
    endDate,
    positionSet: {
      id: assetId,
      type: upperAssetType,
    },
    setLoaded,
    filtersVisible,
    currency,
  }

  return (
    <div className={classNames("modules_analyze-composite-security", { [selectedScreen]: true })}>
      <Paper square className="top-selectors">
        <BaseSelector {...{
          modelId,
          active: Boolean(isActive && selectedBase), // Wrapped in Boolean to avoid prop warning
          portfolioBenchmark: benchmark,
          selectedBase,
          setSelectedBase,
          searchValue: baseSearchValue,
          baseDropdownVisible,
          baseInputFocus,
          baseInputBlur,
          baseHistory,
          baseSearch,
          defaultSelectedBase,
          searchResults,
          alternateLayout: true,
          openCompositeSecurity,
          modelSupportsComposites,
        }}
        />
        <ModelSelector {...{
          alternateLayout: true,
          noSpinner: true,
          disableUnderline: true,
          modelId,
          modelName: selectedModelName,
          setModel,
          resourceType: assetType,
          resourceId: assetId,
          assetAvailability: {
            earliest: earliestDate,
            latest: latestDate,
          },
          warningMessage: unsupportedCurrencyWarning,
        }}
        />
      </Paper>
      <AssetsHeader
        {...{
          account,
          isActive,
          modelId,
          datesWithLabels: true,
          earliestDate,
          startDate: performanceStartDate || startDate,
          endDate,
          latestDate,
          selectedDate,
          period,
          setPeriod,
          resetDate,
          assetId: toLower(assetType) === "etf" ? get(asset, "modelProviderId") : assetId,
          assetType,
          addNotification,
          periodAdjusted,
          selectedDateAdjusted,
          availableRollups,
          currentGrouping,
          setGrouping: selectedScreen === "assets" ? setGrouping : null,
          filter,
          filterOns,
          filtersVisible,
          toggleFilters,
          normalization,
          disableNormalization,
          disableNormalizationTooltip,
          isNormalizationAdjusted,
          setNormalization,
          selectedScreen,
          screens: SCREENS,
          customSelectScreen,
          currency,
          updateFilters,
          addedFilters,
          periodSelectorLabels: selectedScreen === "trends" ? { from: "From", to: "To" } : undefined,
          // Non factor review props
          ...(selectedScreen !== "factors" && {
            setExpansionMode: setExpand,
            isExpanded: expand,
            hasUnexpandedComposites: false,
          }),
          ...(displayShowNotHeld && {
            showNotHeld,
            toggleShowNotHeld: setShowNotHeld,
          }),
        }}
      />
      {selectedScreen === "assets" && <AssetAnalyticsTable
        {...{
          account,
          currentGrouping,
          endDate,
          expand,
          isActive,
          isOpPro,
          modelId,
          performanceStartDate: startDate,
          assetId: positionSetId,
          assetType: upperAssetType,
          segment,
          showNotHeld,
          viewType,
          assetName,
          disableColumnClick: true,
          hideColumnWith: column => column.id === "economicExposure" && (assetType === "etf" || assetType === "index"),
          base,
          baseSignature,
        }}
      />}
      {selectedScreen === "trends" && (selectedSubScreen === "exposure" || selectedSubScreen === "contentset") && <AnalyzeCompositeExposure {...{
        ...commonTrendsAttributes,
        mustAggregateFactors,
        factorsClassification,
        favoriteFactors,
        aggregation: aggregationFunction,
        aggregatedFactorsMetadata,
        factorsMetadata,
        displayAddView,
        selectedViews,
        selectedViewsFactors,
        contentSetId,
      }}
      />}
      {selectedScreen === "trends" && selectedSubScreen === "performance" && <AnalyzeCompositePerformance {...{
        ...commonTrendsAttributes,
        mustAggregateFactors,
        factorsClassification,
        factorsMetadata,
        favoriteFactors,
        displayAddView,
        selectedViews,
      }}
      />}
      {selectedScreen === "trends" && selectedSubScreen === "risk" && <AnalyzeCompositeRisk {...{
        ...commonTrendsAttributes,
        mustAggregateFactors,
        factorsClassification,
        factorsMetadata,
        favoriteFactors,
        period,
        selectedViews,
        displayAddView,
      }}
      />}
      {selectedScreen === "trends" && selectedSubScreen === "composition" && <AnalyzeCompositeComposition {...{
        ...commonTrendsAttributes,
        period,
      }}
      />}
      {selectedScreen === "trends" && selectedSubScreen === "forecast" && <AnalyzeCompositeForecast {...{
        ...commonTrendsAttributes,
        forecastId,
      }}
      />}
      {selectedScreen === "factors" && <div className="factor-review">
        {/* Because subscreen selector is not part of the AnalyzeFactors modules, we need to wait for those module to be loaded before rendering the submenu */}
        {loaded && <FactorsSubscreenSelector {...{
          menuItems: subMenuItems,
          selectedScreen,
          selectedSubScreen,
          selectScreen: (...args) => {
            setLoaded(false)
            selectScreen(...args)
          },
        }}
        />}
        {selectedSubScreen === "performance" && <AnalyzeFactorsPerformance {...commonFactorReviewAttributes} />}
        {selectedSubScreen === "risk" && <AnalyzeFactorsRisk {...commonFactorReviewAttributes} isComposite />}
      </div>}
      {selectedSubScreen === "factor-sensitivity" && <AnalyzeCompositeFactorSensitivity {...{
        ...commonTrendsAttributes,
        mustAggregateFactors,
        factorsClassification,
        favoriteFactors,
        aggregation: aggregationFunction,
        aggregatedFactorsMetadata,
        factorsMetadata,
        displayAddView,
        selectedViews,
        selectedViewsFactors,
        factorSensitivityType,
      }}
      />}
    </div>
  )
}

AnalyzeCompositeSecurity.getHeaderConfig = ({ asset, assetInfoContent, modelId, shareMyViewProps }) => {
  let title = get(asset, "name") || "Composite Asset"

  if (assetInfoContent) {
    title = <div id="composite-asset-info">
      {title}
      <OPTooltip title={assetInfoContent}>
        <InfoOutlined id="composite-asset-info-icon" fontSize="small" />
      </OPTooltip>
    </div>
  }

  const key = asset ? `composite-asset-${asset.name}-${modelId}-${JSON.stringify(shareMyViewProps)}` : "composite-asset"
  return {
    title,
    key,
    noShadow: true,
    modelId,
    actions: { rightActions: [<ShareMyView key="share-my-view" {...shareMyViewProps} />] },
  }
}

AnalyzeCompositeSecurity.propTypes = {
  account: PropTypes.string.isRequired,
  modelId: PropTypes.string.isRequired,
  asset: PropTypes.object,
  displayAddView: PropTypes.bool,
  selectedViews: PropTypes.array,
  selectedViewsFactors: PropTypes.array,
  mustAggregateFactors: PropTypes.bool,
  factorsClassification: PropTypes.object,
  factorsMetadata: PropTypes.object,
  aggregatedFactorsMetadata: PropTypes.object,
  aggregationFunction: PropTypes.string,
  categories: PropTypes.array,
  assetId: PropTypes.string.isRequired,
  assetType: PropTypes.string.isRequired,
  assetName: PropTypes.string,
  assetInfoContent: PropTypes.string,
  internalId: PropTypes.string,
  resourceId: PropTypes.string,
  classifications: PropTypes.array,
  classificationsValues: PropTypes.object,
  base: PropTypes.object,
  baseStartDate: DATE,
  baseSignature: PropTypes.string,
  baseHistory: PropTypes.array.isRequired,
  contentSetId: PropTypes.string,
  forecastId: PropTypes.string,
  latestDate: DATE.isRequired,
  earliestDate: DATE.isRequired,
  startDate: DATE.isRequired,
  endDate: DATE.isRequired,
  selectedDate: DATE,
  driftDates: PropTypes.object,
  setDate: PropTypes.func.isRequired,
  period: PERIOD.isRequired,
  setPeriod: PropTypes.func.isRequired,
  periodAdjusted: PropTypes.bool.isRequired,
  selectedDateAdjusted: PropTypes.bool,
  addNotification: PropTypes.func.isRequired,
  filter: PropTypes.object,
  filterOns: PropTypes.arrayOf(FILTER_ON_GROUP).isRequired,
  filtersVisible: PropTypes.bool.isRequired,
  toggleFilters: PropTypes.func.isRequired,
  segment: PropTypes.object,
  openCompositeSecurity: PropTypes.func.isRequired,
  availableRollups: PropTypes.array.isRequired,
  setGrouping: PropTypes.func.isRequired,
  currentGrouping: PropTypes.object,
  expand: PropTypes.bool,
  setExpand: PropTypes.func.isRequired,
  assetTickerMic: PropTypes.object,
  normalization: PropTypes.string,
  disableNormalization: PropTypes.bool,
  disableNormalizationTooltip: PropTypes.string,
  isNormalizationAdjusted: PropTypes.bool,
  setNormalization: PropTypes.func.isRequired,
  selectScreen: PropTypes.func.isRequired,
  selectedScreen: PropTypes.string.isRequired,
  selectedSubScreen: PropTypes.string.isRequired,
  setModel: PropTypes.func.isRequired,
  selectedModelName: PropTypes.string,
  isActive: PropTypes.bool.isRequired,
  benchmark: PropTypes.object.isRequired,
  selectedBase: PropTypes.object,
  setSelectedBase: PropTypes.func.isRequired,
  baseSearchValue: PropTypes.string,
  baseDropdownVisible: PropTypes.bool.isRequired,
  baseInputFocus: PropTypes.func.isRequired,
  baseInputBlur: PropTypes.func.isRequired,
  baseSearch: PropTypes.func.isRequired,
  defaultSelectedBase: PropTypes.object.isRequired,
  searchResults: PropTypes.array.isRequired,
  viewType: PropTypes.string.isRequired,
  showNotHeld: PropTypes.bool.isRequired,
  setShowNotHeld: PropTypes.func.isRequired,
  displayShowNotHeld: PropTypes.bool.isRequired,
  isOpPro: PropTypes.bool.isRequired,
  resetDate: PropTypes.func.isRequired,
  analyzeMenuItems: PropTypes.array.isRequired,
  menuItems: PropTypes.arrayOf(SEARCHABLE_MENU_ITEM).isRequired,
  subMenuItems: PropTypes.arrayOf(SEARCHABLE_MENU_ITEM).isRequired,
  favoriteFactors: PropTypes.object,
  modelSupportsComposites: PropTypes.bool.isRequired,
  performanceStartDate: PropTypes.string,
  currency: PropTypes.string,
  unsupportedCurrencyWarning: PropTypes.string,
  selectedAnalyzeView: PropTypes.string.isRequired,
  factorSensitivityType: PropTypes.string,
  isModelInQueryString: PropTypes.bool.isRequired,
  shareMyViewProps: PropTypes.object.isRequired,
  setHeader: PropTypes.func.isRequired,
  saveSelectedBase: PropTypes.func.isRequired,
  updateFilters: PropTypes.func.isRequired,
  addedFilters: PropTypes.arrayOf(FILTER),
}

export default withModelAccessControl(saveRecentResources(AnalyzeCompositeSecurity, "resourceId", { condition: props => props.assetType === "basket" }))
